import { Meta, theme } from 'aimo-ui';
import CloseButton from 'components/common/CloseButton';
import InfoBox from 'components/common/InfoBox';
import SvgImage from 'components/svgImage/SvgImage';
import PriceEstimateContext from 'context/PriceEstimateContext';
import { isAfter, isBefore } from 'date-fns';
import { DurationSuggestion } from 'hooks/useSuggestedPricingEstimates';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import Animated, { FadeIn, FadeOut } from 'react-native-reanimated';
import { Shadow } from 'react-native-shadow-2';
import { useParkingStore } from 'stores/parkingStore';
import { SimpleFullContainer } from 'styles/ContainerStyles';
import { PriceEstimate } from 'types/generatedSchemaTypes';
import { getLocale } from 'utils/commonUtils';
import { handlePrefixedStartorEndtime } from 'utils/timeUtils';

export type PriceInfoProps = {
  suggestedDurations: DurationSuggestion[];
  currentEndTime?: Date;
  disableShadow?: boolean;
  showPricingCallback?: (show: boolean) => void;
};

const defaultPrice = {
  totalAmount: 0,
};

const PricingInfoBox: React.FC<PriceInfoProps> = ({
  suggestedDurations,
  currentEndTime = new Date(),
  disableShadow,
  showPricingCallback,
}) => {
  const { t } = useTranslation();
  const currentTime = useMemo(() => new Date(), []);
  const { priceEstimate } = useContext(PriceEstimateContext);
  const { parkingZoneCountryCode } = useParkingStore(
    (state) => state.parkingConditions
  );
  const [pricingEstimate, setPricingEstimate] = useState<PriceEstimate | null>(
    defaultPrice
  );
  const [show, setShow] = useState(true);
  const locale = getLocale();

  useEffect(() => {
    if (priceEstimate !== undefined) {
      setPricingEstimate(priceEstimate);
    }
  }, [priceEstimate]);

  const setHoursToDate = useCallback(
    (date: Date, timeString: string) => {
      if (!timeString) {
        return date;
      }
      return handlePrefixedStartorEndtime(
        timeString,
        date,
        parkingZoneCountryCode
      );
    },
    [parkingZoneCountryCode]
  );

  const suitableDuration = useMemo(
    () =>
      suggestedDurations.find((duration) => {
        const itemStartTime = setHoursToDate(
          currentTime,
          duration.startTime as string
        );
        const itemEndTime = setHoursToDate(
          currentTime,
          duration.endTime as string
        );

        if (
          isBefore(itemEndTime, itemStartTime) &&
          itemEndTime.toDateString() === itemStartTime.toDateString()
        ) {
          itemEndTime.setDate(itemEndTime.getDate() + 1);
        }
        return (
          isAfter(currentEndTime, itemStartTime) &&
          isBefore(currentEndTime, itemEndTime) &&
          pricingEstimate?.totalAmount === duration.estimatedPrice
        );
      }),
    [
      suggestedDurations,
      pricingEstimate?.totalAmount,
      currentEndTime,
      currentTime,
      setHoursToDate,
    ]
  );

  const pricingInfoText = (durationSuggestion: DurationSuggestion) => {
    const itemEndTime = setHoursToDate(
      currentTime,
      durationSuggestion.endTime as string
    );
    const endTime = itemEndTime.toLocaleTimeString(locale.code, {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
    return {
      baseText: t('price.priceInfo', { endTime }),
    };
  };

  useEffect(
    () => showPricingCallback?.(!!suitableDuration && show),
    [showPricingCallback, suitableDuration, show]
  );

  if (!suitableDuration || !show) {
    return null;
  }
  return (
    <Animated.View
      style={[
        styles.container,
        { position: disableShadow ? 'relative' : 'absolute' },
      ]}
      exiting={FadeOut.duration(200)}
      entering={FadeIn.duration(300)}>
      <Shadow stretch style={styles.shadow} disabled={disableShadow}>
        <InfoBox
          padding={15}
          margin={0}
          bgColor={theme.colors.lightBg.green}
          rightComponent={
            <View style={styles.closeButton}>
              <CloseButton
                buttonType="primary"
                onPress={() => setShow(false)}
              />
            </View>
          }
          leftComponent={
            <SvgImage size="l" name="Lamp" testID={'pricing-info'} />
          }>
          <SimpleFullContainer style={styles.simpleContainer}>
            <Meta>{pricingInfoText(suitableDuration).baseText}</Meta>
          </SimpleFullContainer>
        </InfoBox>
      </Shadow>
    </Animated.View>
  );
};

export default React.memo(PricingInfoBox);

const styles = StyleSheet.create({
  shadow: {
    borderRadius: 20,
  },
  container: {
    position: 'absolute',
    width: '100%',
    zIndex: 999,
    top: 10,
  },
  closeButton: {
    position: 'absolute',
    bottom: '5%',
    right: -20,
  },
  simpleContainer: {
    minHeight: 50,
    justifyContent: 'center',
    marginRight: 20,
  },
});
